import cuid from "cuid";
import { get, isEmpty, isNil } from "lodash";
import { BOOLEAN_FIELD_VALUES } from "utils/helpers/booleanField";
import {
  AGE_RULES,
  FIELD_INPUT_TYPES,
  FIELD_VALIDATION_LIMITS,
  TODAY_BOUND,
  VALIDATORS,
} from "utils/validation-rules";
import { buildFormFieldFromVariable } from "./catalog";
import { fieldPathOf, SLOT_TYPES } from "./workshop-view";

const NEW_DEFINITION = { strict: true, fields: [] };

const CONSENT_MESSAGE_KEY = "CONSENT_REQUIRED";

const CHECKED_VALUES = [BOOLEAN_FIELD_VALUES.YES, "true", true];

const DEFAULT_MAX_AGE = 70;

export const CHECK_KINDS = {
  TEXT: "text",
  EMAIL: "email",
  PHONE: "phone",
  DATE: "date",
  NUMBER: "number",
  CHOICE: "choice",
  CHECKBOX: "checkbox",
  FILE: "file",
};

const KIND_BY_INPUT = {
  checkbox: CHECK_KINDS.CHECKBOX,
  radioGroup: CHECK_KINDS.CHOICE,
  select: CHECK_KINDS.CHOICE,
  file: CHECK_KINDS.FILE,
  email: CHECK_KINDS.EMAIL,
  tel: CHECK_KINDS.PHONE,
  date: CHECK_KINDS.DATE,
  number: CHECK_KINDS.NUMBER,
  range: CHECK_KINDS.NUMBER,
};

const KIND_BY_TYPE = {
  [FIELD_INPUT_TYPES.TEXT]: CHECK_KINDS.TEXT,
  [FIELD_INPUT_TYPES.EMAIL]: CHECK_KINDS.EMAIL,
  [FIELD_INPUT_TYPES.PHONE]: CHECK_KINDS.PHONE,
  [FIELD_INPUT_TYPES.DATE]: CHECK_KINDS.DATE,
  [FIELD_INPUT_TYPES.NUMBER]: CHECK_KINDS.NUMBER,
  [FIELD_INPUT_TYPES.SLIDER]: CHECK_KINDS.NUMBER,
};

const SHAPE_KINDS = [
  CHECK_KINDS.CHECKBOX,
  CHECK_KINDS.CHOICE,
  CHECK_KINDS.FILE,
];

export const RULES_BY_KIND = {
  [CHECK_KINDS.TEXT]: [
    VALIDATORS.MIN_LENGTH,
    VALIDATORS.MAX_LENGTH,
    VALIDATORS.REGEX,
    VALIDATORS.NIR,
    VALIDATORS.IBAN,
    VALIDATORS.BIC,
    VALIDATORS.CITY_NAME,
    VALIDATORS.CONTRACT_NUMBER,
  ],
  [CHECK_KINDS.EMAIL]: [
    VALIDATORS.EMAIL,
    VALIDATORS.MAX_LENGTH,
    VALIDATORS.REGEX,
  ],
  [CHECK_KINDS.PHONE]: [VALIDATORS.PHONE, VALIDATORS.REGEX],
  [CHECK_KINDS.DATE]: [
    VALIDATORS.MIN_AGE,
    VALIDATORS.MAX_AGE,
    VALIDATORS.DATE_MIN,
    VALIDATORS.DATE_MAX,
  ],
  [CHECK_KINDS.NUMBER]: [VALIDATORS.MIN, VALIDATORS.MAX],
  [CHECK_KINDS.CHOICE]: [VALIDATORS.ONE_OF],
  [CHECK_KINDS.CHECKBOX]: [],
  [CHECK_KINDS.FILE]: [],
};

const RULE_DEFAULTS = {
  [VALIDATORS.MIN_LENGTH]: { value: FIELD_VALIDATION_LIMITS.NAME_MIN_LENGTH },
  [VALIDATORS.MAX_LENGTH]: { value: FIELD_VALIDATION_LIMITS.NAME_MAX_LENGTH },
  [VALIDATORS.REGEX]: { pattern: "" },
  [VALIDATORS.MIN]: { value: FIELD_VALIDATION_LIMITS.POSITIVE_NUMBER_MIN },
  [VALIDATORS.MAX]: { value: FIELD_VALIDATION_LIMITS.NUMBER_MAX_PLACEHOLDER },
  [VALIDATORS.MIN_AGE]: {
    value: FIELD_VALIDATION_LIMITS.ADULT_MIN_AGE,
    ageRule: AGE_RULES.AGE_ON_EFFECTIVE_DATE,
  },
  [VALIDATORS.MAX_AGE]: {
    value: DEFAULT_MAX_AGE,
    ageRule: AGE_RULES.AGE_ON_EFFECTIVE_DATE,
  },
  [VALIDATORS.DATE_MIN]: { value: { ...TODAY_BOUND } },
  [VALIDATORS.DATE_MAX]: { value: { ...TODAY_BOUND } },
  [VALIDATORS.ONE_OF]: { values: [] },
};

export const checkKindOf = (slot, variable) => {
  const fromInput = KIND_BY_INPUT[get(slot, "inputKind")];
  if (SHAPE_KINDS.includes(fromInput)) return fromInput;
  return KIND_BY_TYPE[get(variable, "type")] || fromInput || CHECK_KINDS.TEXT;
};

export const newRule = (validator) => ({
  id: cuid(),
  validator,
  ...(RULE_DEFAULTS[validator] || {}),
});

const isConsentRule = (rule) =>
  get(rule, "validator") === VALIDATORS.ONE_OF &&
  (get(rule, "values") || []).includes(BOOLEAN_FIELD_VALUES.YES);

export const isMustBeChecked = (check) =>
  Boolean(get(check, "required")) &&
  (get(check, "rules") || []).some(isConsentRule);

export const withMustBeChecked = (check, on) => ({
  ...check,
  required: on,
  rules: [
    ...(get(check, "rules") || []).filter((rule) => !isConsentRule(rule)),
    ...(on
      ? [
          {
            id: cuid(),
            validator: VALIDATORS.ONE_OF,
            values: CHECKED_VALUES,
            messageKey: CONSENT_MESSAGE_KEY,
          },
        ]
      : []),
  ],
});

export const shownRules = (check) =>
  (get(check, "rules") || []).filter((rule) => !isConsentRule(rule));

const fieldsOf = (formDefinition) => get(formDefinition, "fields") || [];

export const checkVariableOf = (slot, variables) => {
  if (get(slot, "type") !== SLOT_TYPES.INPUT) return null;
  if (!isEmpty(get(slot, "collection"))) return null;
  const path = fieldPathOf(slot);
  if (isNil(path)) return null;
  return (variables || []).find((variable) => variable.field === path) || null;
};

export const checkOf = (formDefinition, variable) =>
  fieldsOf(formDefinition).find(
    (field) => field.accessor === get(variable, "accessor"),
  ) || null;

export const draftCheckOf = (formDefinition, variable) =>
  checkOf(formDefinition, variable) || buildFormFieldFromVariable(variable);

export const withCheck = (formDefinition, check) => {
  const base = formDefinition || NEW_DEFINITION;
  const fields = fieldsOf(base);
  const exists = fields.some((field) => field.accessor === check.accessor);
  return {
    ...base,
    fields: exists
      ? fields.map((field) =>
          field.accessor === check.accessor ? check : field,
        )
      : [...fields, check],
  };
};

export const withoutCheck = (formDefinition, accessor) => ({
  ...(formDefinition || NEW_DEFINITION),
  fields: fieldsOf(formDefinition).filter(
    (field) => field.accessor !== accessor,
  ),
});

export const withStrict = (formDefinition, strict) => ({
  ...(formDefinition || NEW_DEFINITION),
  strict,
});

export const isStrict = (formDefinition) =>
  Boolean(get(formDefinition, "strict"));

export const hasChecks = (formDefinition) =>
  fieldsOf(formDefinition).some((field) => !field.display);

export const orphanChecks = (formDefinition, slots, variables) => {
  const shown = new Set(
    (slots || [])
      .map((slot) => checkVariableOf(slot, variables))
      .filter(Boolean)
      .map((variable) => variable.accessor),
  );
  return fieldsOf(formDefinition).filter(
    (field) => !field.display && !shown.has(field.accessor),
  );
};

export const ruleMessage = (rule, labels) => {
  const template = get(labels.RULES, [rule.validator, "MESSAGE"]);
  if (!template) return undefined;
  return template.replace("{{value}}", rule.value ?? "");
};
